import React, { Fragment } from 'react';
import type { FieldArrayRenderProps } from 'react-final-form-arrays';
import CheckboxField, { CheckboxFieldProps } from '../CheckboxField/CheckboxField';
import { ListBoxOption } from '../form.styles';

export type OptionType = Omit<CheckboxFieldProps, 'id' | 'checked' | 'onChange'> & {
  id?: string;
  value: string | number;
  label: string | React.ReactNode;
};

export type DropdownCheckboxListOptionsProps = FieldArrayRenderProps<
  string | number,
  HTMLDivElement
> & {
  /**
   * The options rendered in the checkbox list.
   */
  options: OptionType[];

  /**
   * ID of the form control.  Will be used to generate sequential ids for each
   * checkbox in the list (if not provided in the option)
   */
  id: string;
};

/**
 * A list of checkbox options, as it appears in the DropdownCheckboxList.  For
 * re-use outside the context of a dropdown.  Suggested use is within the `<ListBox>`
 * component to properly style list elements.
 *
 *
 * ## Example
 * ```typescript
 * import { FieldArray } from 'react-final-form-arrays';
 * import arrayMutators from 'final-form-arrays';
 *
 * import { DropdownCheckboxListOptions } from '@zola/zola-ui/src/components/Form/DropdownCheckboxList/DropdownCheckboxListOptions';
 * import { ListBox } from '@zola/zola-ui/src/components/Form/common/DropdownListBox/DropdownListBox.styles';
 *
 * const COLORS:OptionType[] [
 *  { label: 'Red', value: '#F00' },
 *  { label: 'Green', value: '#0F0' },
 *  { label: 'Blue', value: '#00F' },
 *  { label: 'White', value: '#FFF', disabled: true },
 *  { label: 'Black', value: '#000' },
 * ];
 *
 * <Form
 *   mutators={{
 *     ...arrayMutators
 *   }}
 *   render={() => {
 *     return (
 *       <form>
 *         <FieldArray name="colors">
 *           {({ ...fieldProps }) => (
 *             <ListBox>
 *               <DropdownCheckboxListOptions options={COLORS} {...fieldProps} />
 *             </ListBox>
 *           )}
 *         </FieldArray>
 *       </form>
 *     )
 *   }}
 * />
 * ```
 */
export const DropdownCheckboxListOptions = ({
  fields,
  options,
  id,
}: DropdownCheckboxListOptionsProps): JSX.Element => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>, value: string | number) => {
    if (e.target.checked) {
      fields.push(value);
    } else {
      fields.remove(fields.value.indexOf(value));
    }
  };

  return (
    <Fragment>
      {options?.map((option, i) => {
        const { value, label: checkboxLabel, ...rest } = option;
        return (
          <ListBoxOption key={`${id}-${i}`}>
            <CheckboxField
              id={`${id}-${i}`}
              label={checkboxLabel}
              checked={fields?.value?.includes(value)}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e, value)}
              {...rest}
            />
          </ListBoxOption>
        );
      })}
    </Fragment>
  );
};
